import * as Tone from "tone";

const ADDITIONAL_FATBOY_SOUND_IDS = [
  "accordion", "acoustic_guitar_steel", "alto_sax", "applause", "bagpipe", "banjo", "baritone_sax", "bassoon", "blown_bottle", "brass_section",
  "bright_acoustic_piano", "celesta", "cello", "choir_aahs", "church_organ", "clarinet", "contrabass", "dulcimer", "electric_bass_finger",
  "electric_bass_pick", "electric_grand_piano", "english_horn", "fiddle", "flute", "french_horn", "fretless_bass", "glockenspiel", "harmonica",
  "honkytonk_piano", "kalimba", "koto", "lead_1_square", "lead_2_sawtooth", "lead_3_calliope", "lead_4_chiff", "lead_5_charang",
  "lead_6_voice", "lead_7_fifths", "lead_8_bass__lead", "marimba", "melodic_tom", "music_box", "muted_trumpet", "oboe", "ocarina",
  "orchestral_harp", "overdriven_guitar", "pad_1_new_age", "pad_2_warm", "pad_3_polysynth", "pad_4_choir", "pad_5_bowed", "pad_6_metallic",
  "pad_7_halo", "pad_8_sweep", "pan_flute", "percussive_organ", "piccolo", "recorder", "reverse_cymbal", "shakuhachi", "shamisen",
  "shanai", "sitar", "slap_bass_1", "slap_bass_2", "soprano_sax", "steel_drums", "string_ensemble_1", "string_ensemble_2",
  "synth_bass_1", "synth_bass_2", "synth_brass_1", "synth_brass_2", "synth_choir", "synth_drum", "synth_strings_1", "synth_strings_2",
  "tango_accordion", "tenor_sax", "trombone", "trumpet", "tuba", "tubular_bells", "viola", "violin",
];
const SOUND_LABEL_WORD_OVERRIDES = { aahs: "Aahs", bass: "Bass", sax: "Sax" };
const soundfontScriptPromises = new Map();
const audioState = {
  sampler: null,
  soundBank: "",
  ready: false,
  loadPromise: null,
};

export const STRUM_STEP_SECONDS = 0.018;
export const FATBOY_SOUNDFONTS = [
  { id: "acoustic_grand_piano", label: "Acoustic Grand Piano", globalName: "acoustic_grand_piano", gain: 0.9, defaultVelocity: 0.72, release: 1.2 },
  { id: "electric_piano_1", label: "Electric Piano 1", globalName: "electric_piano_1", gain: 0.95, defaultVelocity: 0.68, release: 1.6 },
  { id: "electric_guitar_clean", label: "Electric Guitar Clean", globalName: "electric_guitar_clean", gain: 0.9, defaultVelocity: 0.72, release: 1.1 },
  { id: "acoustic_guitar_nylon", label: "Acoustic Guitar Nylon", globalName: "acoustic_guitar_nylon", gain: 0.95, defaultVelocity: 0.72, release: 1.2 },
  { id: "acoustic_bass", label: "Acoustic Bass", globalName: "acoustic_bass", gain: 0.95, defaultVelocity: 0.72, release: 1.0 },
  { id: "clavinet", label: "Clavinet", globalName: "clavinet", gain: 0.9, defaultVelocity: 0.72, release: 0.7 },
  { id: "distortion_guitar", label: "Distortion Guitar", globalName: "distortion_guitar", gain: 0.82, defaultVelocity: 0.66, release: 1.2 },
  { id: "drawbar_organ", label: "Drawbar Organ", globalName: "drawbar_organ", gain: 0.75, defaultVelocity: 0.62, release: 0.8 },
  { id: "electric_guitar_jazz", label: "Electric Guitar Jazz", globalName: "electric_guitar_jazz", gain: 0.9, defaultVelocity: 0.7, release: 1.1 },
  { id: "electric_guitar_muted", label: "Electric Guitar Muted", globalName: "electric_guitar_muted", gain: 0.95, defaultVelocity: 0.72, release: 0.6 },
  { id: "electric_piano_2", label: "Electric Piano 2", globalName: "electric_piano_2", gain: 0.95, defaultVelocity: 0.68, release: 1.6 },
  { id: "harpsichord", label: "Harpsichord", globalName: "harpsichord", gain: 0.85, defaultVelocity: 0.72, release: 0.7 },
  { id: "pizzicato_strings", label: "Pizzicato Strings", globalName: "pizzicato_strings", gain: 0.9, defaultVelocity: 0.7, release: 0.8 },
  { id: "reed_organ", label: "Reed Organ", globalName: "reed_organ", gain: 0.78, defaultVelocity: 0.62, release: 0.8 },
  { id: "rock_organ", label: "Rock Organ", globalName: "rock_organ", gain: 0.78, defaultVelocity: 0.62, release: 0.8 },
  { id: "vibraphone", label: "Vibraphone", globalName: "vibraphone", gain: 0.9, defaultVelocity: 0.68, release: 1.8 },
  { id: "woodblock", label: "Woodblock", globalName: "woodblock", gain: 0.9, defaultVelocity: 0.72, release: 0.4 },
  { id: "xylophone", label: "Xylophone", globalName: "xylophone", gain: 0.9, defaultVelocity: 0.7, release: 0.7 },
  ...ADDITIONAL_FATBOY_SOUND_IDS.map((id) => ({ id, label: soundfontLabelFromId(id), globalName: id, gain: 0.9, defaultVelocity: 0.68, release: 1.2 })),
];

export function soundfontLabelFromId(id) {
  return id
    .split("_")
    .filter(Boolean)
    .map((word) => SOUND_LABEL_WORD_OVERRIDES[word] || `${word[0].toUpperCase()}${word.slice(1)}`)
    .join(" ");
}

export function normalizeSoundfontId(value, fallback = "acoustic_grand_piano") {
  const raw = String(value || "").trim();
  return FATBOY_SOUNDFONTS.some((config) => config.id === raw) ? raw : fallback;
}

export function soundfontConfig(id = "acoustic_grand_piano") {
  return FATBOY_SOUNDFONTS.find((config) => config.id === normalizeSoundfontId(id)) || FATBOY_SOUNDFONTS[0];
}

export function renderSoundBankOptions(select, selectedSoundBank = "acoustic_grand_piano") {
  select.replaceChildren(...FATBOY_SOUNDFONTS.map((soundfont) => {
    const option = document.createElement("option");
    option.value = soundfont.id;
    option.textContent = soundfont.label;
    return option;
  }));
  select.value = normalizeSoundfontId(selectedSoundBank);
}

function soundfontScriptSrc(soundBank) {
  return `/sounds/fatboy/${soundfontConfig(soundBank).globalName}-ogg.js`;
}

async function ensureSoundfontData(soundBank) {
  const config = soundfontConfig(soundBank);
  if (window.MIDI?.Soundfont?.[config.globalName]) {
    return;
  }

  const src = soundfontScriptSrc(soundBank);
  const existing = soundfontScriptPromises.get(src);
  if (existing) {
    await existing;
    return;
  }

  const loadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => {
      if (window.MIDI?.Soundfont?.[config.globalName]) {
        resolve();
        return;
      }
      reject(new Error(`Sound bank loaded without data: ${config.label}`));
    };
    script.onerror = () => reject(new Error(`Could not load sound bank: ${config.label}`));
    document.head.append(script);
  });
  soundfontScriptPromises.set(src, loadPromise);
  await loadPromise;
}

export async function ensureSampler(soundBank = "acoustic_grand_piano") {
  await Tone.start();
  const config = soundfontConfig(soundBank);
  if (audioState.ready && audioState.sampler && audioState.soundBank === config.id) {
    return audioState;
  }
  if (audioState.loadPromise && audioState.soundBank === config.id) {
    await audioState.loadPromise;
    return audioState;
  }

  await ensureSoundfontData(config.id);
  const soundfont = window.MIDI?.Soundfont?.[config.globalName];
  if (!soundfont) {
    throw new Error(`Sound bank is not loaded: ${config.label}`);
  }

  const output = new Tone.Gain(config.gain).toDestination();
  audioState.soundBank = config.id;
  audioState.ready = false;
  audioState.sampler = new Tone.Sampler({
    urls: { ...soundfont },
    attack: 0,
    release: config.release,
    onload() {
      audioState.ready = true;
    },
  }).connect(output);
  audioState.loadPromise = Tone.loaded().then(() => {
    audioState.ready = true;
    return audioState;
  });
  await audioState.loadPromise;
  return audioState;
}

export function releaseActiveSampler(time = Tone.now(), { dispose = false } = {}) {
  audioState.sampler?.releaseAll(time);
  if (dispose && audioState.sampler) {
    audioState.sampler.dispose();
    audioState.sampler = null;
    audioState.ready = false;
    audioState.loadPromise = null;
  }
}

export async function playMidi(midi, { soundBank = "acoustic_grand_piano", duration = 0.7, triggerElement = null } = {}) {
  const samplerState = await ensureSampler(soundBank);
  samplerState.sampler?.triggerAttackRelease(
    Tone.Frequency(midi, "midi").toNote(),
    duration,
    Tone.now(),
    soundfontConfig(soundBank).defaultVelocity,
  );
  flashElement(triggerElement);
}

export async function playChordMidis(midis, { soundBank = "acoustic_grand_piano", playStyle = "strum", duration = 1.25 } = {}) {
  const samplerState = await ensureSampler(soundBank);
  const now = Tone.now();
  midis.forEach((midi, index) => {
    samplerState.sampler?.triggerAttackRelease(
      Tone.Frequency(midi, "midi").toNote(),
      duration,
      now + (playStyle === "strum" ? index * STRUM_STEP_SECONDS : 0),
      soundfontConfig(soundBank).defaultVelocity,
    );
  });
}

function flashElement(element) {
  if (!element) {
    return;
  }
  element.classList.remove("is-playing");
  void element.offsetWidth;
  element.classList.add("is-playing");
  window.setTimeout(() => element.classList.remove("is-playing"), 260);
}
